import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";
import {
  addProductToCart,
  clearCurrentCart,
  getCartStepForProduct,
  getSuggestedCartAmount,
  loadCart,
  removeCartItem,
  updateCartItemQuantity,
  CART_RESERVATION_MINUTES,
} from "../../data/cart";
import { formatProductAmount, loadCatalog } from "../../data/catalog";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} EUR`;

const ShopCart = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [cart, setCart] = useState({ id: "", items: [], reservedUntil: "" });
  const [notice, setNotice] = useState("");

  const refreshCart = async () => {
    const loadedCart = await loadCart();

    if (!loadedCart.items.length) {
      const catalog = await loadCatalog();
      if (catalog.length > 0) {
        try {
          await addProductToCart(catalog[0], getSuggestedCartAmount(catalog[0]));
          const nextCart = await loadCart();
          setCart(nextCart);
          return nextCart;
        } catch (error) {
          setNotice(error.message);
        }
      }
    }

    setCart(loadedCart);
    return loadedCart;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCart = async () => {
      const loadedCart = await loadCart();

      if (!isMounted) {
        return;
      }

      if (!loadedCart.items.length) {
        const catalog = await loadCatalog();
        if (catalog.length > 0) {
          try {
            await addProductToCart(catalog[0], getSuggestedCartAmount(catalog[0]));
            const nextCart = await loadCart();
            if (isMounted) {
              setCart(nextCart);
            }
          } catch (error) {
            if (isMounted) {
              setNotice(error.message);
            }
          }
        } else {
          setCart(loadedCart);
        }
      } else {
        setCart(loadedCart);
      }

      if (isMounted) {
        setLoaderStatus(false);
      }
    };

    fetchCart();

    return () => {
      isMounted = false;
    };
  }, []);

  const updateQuantity = async (id, delta, currentQuantity) => {
    try {
      const nextCart = await updateCartItemQuantity(id, currentQuantity + delta);
      setCart(nextCart);
      setNotice("");
    } catch (error) {
      setNotice(error.message);
    }
  };

  const handleRemoveItem = async (id) => {
    const nextCart = await removeCartItem(id);
    setCart(nextCart);
  };

  const handleResetCart = async () => {
    await clearCurrentCart();
    const nextCart = await refreshCart();
    setCart(nextCart);
  };

  const regularItems = useMemo(() => cart.items.filter(item => item.product?.type !== "fresh" && item.product?.type !== "frozen"), [cart.items]);
  const coldItems = useMemo(() => cart.items.filter(item => item.product?.type === "fresh" || item.product?.type === "frozen"), [cart.items]);

  const regularSubtotal = useMemo(() => regularItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0), [regularItems]);
  const coldSubtotal = useMemo(() => coldItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0), [coldItems]);

  const subtotal = regularSubtotal + coldSubtotal;

  const regularDelivery = regularSubtotal >= 49 || regularItems.length === 0 ? 0 : 4.9;
  const coldDelivery = coldItems.length === 0 ? 0 : (coldSubtotal >= 79 ? 3.90 : 6.90);
  const delivery = regularDelivery + coldDelivery;
  const total = subtotal + delivery;

  const reservedUntilLabel = useMemo(() => {
    if (!cart.reservedUntil) {
      return `${CART_RESERVATION_MINUTES} min`;
    }

    return new Date(cart.reservedUntil).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [cart.reservedUntil]);

  return (
    <div>
      <div>
        {loaderStatus ? (
          <div className="container py-8">
            <div className="alert alert-light border mb-0">
              Chargement du panier...
            </div>
          </div>
        ) : (
          <>
            <ScrollToTop />
            <section className="mb-lg-14 mb-8 mt-8">
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <div className="card py-1 border-0 mb-8">
                      <div>
                        <h1 className="fw-bold">Panier</h1>
                        <p className="mb-0">Votre selection Almoune reservee jusqu'a {reservedUntilLabel}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-8 col-md-7">
                    <div className="py-3">
                      {notice ? (
                        <div className="alert alert-warning" role="alert">
                          {notice}
                        </div>
                      ) : null}

                      {/* PANIER EPICERIE */}
                      <div className="mb-4">
                        <h4 className="fw-bold mb-3 border-bottom pb-2">Panier Epicerie</h4>
                        <div className="alert alert-info p-2 mb-3" role="alert">
                          {regularSubtotal >= 49
                            ? "Livraison standard offerte."
                            : `Livraison standard offerte a partir de 49 EUR (manque ${(49 - regularSubtotal).toFixed(2)} EUR).`}
                        </div>
                        <ul className="list-group list-group-flush">
                          {regularItems.map((item) => (
                            <li className="list-group-item py-3 py-lg-0 px-0 border-top" key={item.id}>
                              <div className="row align-items-center">
                                <div className="col-3 col-md-2">
                                  <div className="bg-light rounded-3 d-flex align-items-center justify-content-center" style={{ minHeight: "90px" }}>
                                    <span className="small text-muted text-center px-2">
                                      {item.title}
                                    </span>
                                  </div>
                                </div>
                                <div className="col-4 col-md-6">
                                  <h6 className="mb-0">{item.title}</h6>
                                  <span>
                                    <small className="text-muted">{item.unit}</small>
                                  </span>
                                  <div className="mt-2 small text-muted">
                                    Stock reserve: {item.isWeighted ? formatProductAmount(item.product, item.reservedQuantity) : item.reservedQuantity}
                                  </div>
                                  {item.product?.maxPerOrder ? (
                                    <div className="mt-1 small">
                                      <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                                        Max {item.product.maxPerOrder} par commande
                                      </span>
                                    </div>
                                  ) : null}
                                  {item.isWeighted ? (
                                    <div className="mt-1 small text-muted">
                                      Poids demande: {formatProductAmount(item.product, item.requestedWeightKg || item.quantity)}
                                    </div>
                                  ) : null}
                                  <div className="mt-2 small">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveItem(item.id)}
                                      className="btn btn-link p-0 text-decoration-none text-inherit"
                                    >
                                      <span className="text-muted">Retirer</span>
                                    </button>
                                  </div>
                                </div>
                                <div className="col-3 col-md-3 col-lg-2">
                                  <div className="input-group flex-nowrap justify-content-center">
                                    <button
                                      type="button"
                                      className="button-minus form-control text-center flex-xl-none w-xl-30 w-xxl-10 px-0"
                                      onClick={() => updateQuantity(item.id, -getCartStepForProduct(item.product), item.quantity)}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      step={item.isWeighted ? getCartStepForProduct(item.product) : 1}
                                      max={99}
                                      value={item.quantity}
                                      readOnly
                                      className="quantity-field form-control text-center flex-xl-none w-xl-30 w-xxl-10 px-0"
                                    />
                                    <button
                                      type="button"
                                      className="button-plus form-control text-center flex-xl-none w-xl-30 w-xxl-10 px-0"
                                      onClick={() => updateQuantity(item.id, getCartStepForProduct(item.product), item.quantity)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                                <div className="col-2 text-lg-end text-start text-md-end col-md-2">
                                  <span className="fw-bold">{formatPrice(item.lineTotal)}</span>
                                </div>
                              </div>
                            </li>
                          ))}
                          {!regularItems.length ? (
                            <li className="list-group-item py-4 px-0 text-muted">
                              Votre panier epicerie est vide.
                            </li>
                          ) : null}
                        </ul>
                      </div>

                      {/* PANIER FRAIS & SURGELES */}
                      <div className="mb-4 mt-5">
                        <h4 className="fw-bold mb-3 border-bottom pb-2">Panier Frais & Surgeles</h4>
                        <div className={`alert ${coldSubtotal < 49 ? "alert-danger" : "alert-info"} p-2 mb-3`} role="alert">
                          {coldSubtotal >= 79
                            ? "Frais de livraison réduits à 3,90 EUR."
                            : (coldSubtotal >= 49
                              ? `Livraison : 6,90 EUR (Plus que ${(79 - coldSubtotal).toFixed(2)} EUR pour réduire à 3,90 EUR).`
                              : `Minimum de commande de 49 EUR requis pour le frais (manque ${(49 - coldSubtotal).toFixed(2)} EUR).`)}
                        </div>
                        <ul className="list-group list-group-flush">
                          {coldItems.map((item) => (
                            <li className="list-group-item py-3 py-lg-0 px-0 border-top" key={item.id}>
                              <div className="row align-items-center">
                                <div className="col-3 col-md-2">
                                  <div className="bg-light rounded-3 d-flex align-items-center justify-content-center" style={{ minHeight: "90px" }}>
                                    <span className="small text-muted text-center px-2">
                                      {item.title}
                                    </span>
                                  </div>
                                </div>
                                <div className="col-4 col-md-6">
                                  <h6 className="mb-0">{item.title}</h6>
                                  <span>
                                    <small className="text-muted">{item.unit}</small>
                                  </span>
                                  <div className="mt-2 small text-muted">
                                    Stock reserve: {item.isWeighted ? formatProductAmount(item.product, item.reservedQuantity) : item.reservedQuantity}
                                  </div>
                                  {item.product?.maxPerOrder ? (
                                    <div className="mt-1 small">
                                      <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                                        Max {item.product.maxPerOrder} par commande
                                      </span>
                                    </div>
                                  ) : null}
                                  {item.isWeighted ? (
                                    <div className="mt-1 small text-muted">
                                      Poids demande: {formatProductAmount(item.product, item.requestedWeightKg || item.quantity)}
                                    </div>
                                  ) : null}
                                  <div className="mt-2 small">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveItem(item.id)}
                                      className="btn btn-link p-0 text-decoration-none text-inherit"
                                    >
                                      <span className="text-muted">Retirer</span>
                                    </button>
                                  </div>
                                </div>
                                <div className="col-3 col-md-3 col-lg-2">
                                  <div className="input-group flex-nowrap justify-content-center">
                                    <button
                                      type="button"
                                      className="button-minus form-control text-center flex-xl-none w-xl-30 w-xxl-10 px-0"
                                      onClick={() => updateQuantity(item.id, -getCartStepForProduct(item.product), item.quantity)}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      step={item.isWeighted ? getCartStepForProduct(item.product) : 1}
                                      max={99}
                                      value={item.quantity}
                                      readOnly
                                      className="quantity-field form-control text-center flex-xl-none w-xl-30 w-xxl-10 px-0"
                                    />
                                    <button
                                      type="button"
                                      className="button-plus form-control text-center flex-xl-none w-xl-30 w-xxl-10 px-0"
                                      onClick={() => updateQuantity(item.id, getCartStepForProduct(item.product), item.quantity)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                                <div className="col-2 text-lg-end text-start text-md-end col-md-2">
                                  <span className="fw-bold">{formatPrice(item.lineTotal)}</span>
                                </div>
                              </div>
                            </li>
                          ))}
                          {!coldItems.length ? (
                            <li className="list-group-item py-4 px-0 text-muted">
                              Votre panier frais & surgeles est vide.
                            </li>
                          ) : null}
                        </ul>
                      </div>

                      <div className="d-flex justify-content-between mt-4">
                        <Link to="/Shop" className="btn btn-primary">
                          Continuer vos achats
                        </Link>
                        <button
                          type="button"
                          className="btn btn-outline-dark"
                          onClick={handleResetCart}
                        >
                          Vider le panier
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-4 col-md-5">
                    <div className="mb-5 card mt-6">
                      <div className="card-body p-6">
                        <h2 className="h5 mb-4">Resume de commande</h2>
                        <div className="card mb-2">
                          <ul className="list-group list-group-flush">
                            <li className="list-group-item d-flex justify-content-between align-items-start">
                              <div className="me-auto">
                                <div>Sous-total</div>
                              </div>
                              <span>{formatPrice(subtotal)}</span>
                            </li>
                            <li className="list-group-item d-flex justify-content-between align-items-start">
                              <div className="me-auto">
                                <div>Livraison</div>
                              </div>
                              <span>{delivery === 0 ? "Offerte" : formatPrice(delivery)}</span>
                            </li>
                            <li className="list-group-item d-flex justify-content-between align-items-start">
                              <div className="me-auto">
                                <div className="fw-bold">Total</div>
                              </div>
                              <span className="fw-bold">{formatPrice(total)}</span>
                            </li>
                          </ul>
                        </div>
                        <div className="d-grid mb-1 mt-4">
                          {coldItems.length > 0 && coldSubtotal < 49 ? (
                            <button className="btn btn-secondary btn-lg d-flex justify-content-between align-items-center" disabled title="Le panier frais/surgelés doit atteindre 49 EUR minimum">
                              Minimum Frais 49€ Non Atteint <span className="fw-bold">{formatPrice(total)}</span>
                            </button>
                          ) : (
                            <Link to="/ShopCheckOut" className="btn btn-primary btn-lg d-flex justify-content-between align-items-center">
                              Commander <span className="fw-bold">{formatPrice(total)}</span>
                            </Link>
                          )}
                        </div>
                        <p className="mt-3 mb-0 text-muted small">
                          Les quantites du panier reservent du stock pendant {CART_RESERVATION_MINUTES} minutes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopCart;
